import { I18nProvider } from '@contexts/I18nContext'
import { NotificationProvider, useNotifications } from '@contexts/NotificationContext'
import { Global } from '@emotion/react'
import { KeycloakProvider, useKeycloak } from '@lib/keycloak'
import { isolateMobxGlobalState } from '@lib/mobx'
import { QueryProvider } from '@lib/react-query'
import { initSentry } from '@lib/sentry'
import * as Sentry from '@sentry/react'
import { RootErrorFallback } from '@shared/components/ErrorFallback'
import React, { useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'

import { SuiProvider } from '@hrworks/sui-core'
import { Page } from '@hrworks/sui-core/Page'

import { setKeycloakInstance } from './api/client'
import { setSessionExpiredHandler } from './lib/session/sessionExpired'
import { router } from './routes/AppRoutes'
import { SessionExpiredModal } from './shared/components/SessionExpiredModal/SessionExpiredModal'
import { globalStyles, theme } from './styles'

// Import SmartFace critical styles (CSS variables, fonts, base styles).
// Local copy — see the note in src/styles/critical.css for why the vendored
// file can't be imported directly.
import './styles/critical.css'

// Initialize Sentry before rendering the app. isolateMobxGlobalState() must run
// after this so its foreign-MobX warning is deliverable, and before render so
// MobX has no live reactions (isolateGlobalState() throws otherwise).
initSentry()
isolateMobxGlobalState()

const KeycloakConnector = ({ children }: { children: React.ReactNode }) => {
  const { keycloak } = useKeycloak()

  useEffect(() => {
    if (keycloak) {
      setKeycloakInstance(keycloak)
    }
  }, [keycloak])

  return <>{children}</>
}

// Wires the session-expired handler on mount so client.ts and KeycloakProvider
// can trigger the modal without a direct React dependency.
const SessionExpiredBridge = ({ children }: { children: React.ReactNode }) => {
  const [sessionExpired, setSessionExpired] = useState(false)
  const { keycloak } = useKeycloak()

  useEffect(() => {
    setSessionExpiredHandler(() => setSessionExpired(true))
    return () => setSessionExpiredHandler(null)
  }, [])

  return (
    <>
      {children}
      {sessionExpired && <SessionExpiredModal onConfirm={() => void keycloak?.login()} />}
    </>
  )
}

// Reads the live notification array from context and passes it to Page so the
// SmartFace layout renders toast notifications.
const NotificationBridge = ({ children }: { children: React.ReactNode }) => {
  const { notifications } = useNotifications()
  return (
    <Page modals={[]} notifications={notifications}>
      {children}
    </Page>
  )
}

const root = createRoot(document.getElementById('root') as HTMLElement)

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={RootErrorFallback} showDialog={false}>
      <KeycloakProvider>
        <SuiProvider customTheme={theme} useCustomScrollbars={true} iconSet="streamline" theme="light">
          <Global styles={globalStyles(theme)} />
          <I18nProvider>
            <NotificationProvider>
              <QueryProvider>
                <KeycloakConnector>
                  <SessionExpiredBridge>
                    <NotificationBridge>
                      <RouterProvider router={router} />
                    </NotificationBridge>
                  </SessionExpiredBridge>
                </KeycloakConnector>
              </QueryProvider>
            </NotificationProvider>
          </I18nProvider>
        </SuiProvider>
      </KeycloakProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
)
